/**
 * The standing brief's limits and the sentences that hold them, in one place.
 *
 * The brief is capped at twelve directives of 240 characters, and the cap used to
 * live only in the codec (`asBrief`), which is the one place nobody is looking:
 * a thirteenth given typed with a seed stood on screen, was sent to every pass
 * of the session, and was silently gone after a reload — the prompts a node was
 * reasoned under then named a stipulation the document no longer held. A
 * directive past 240 characters was cut the same way. So the two writers that
 * add directives — the composer's typed `/given`, `/constraint` and friends, and
 * a gap's "Answer it" — ask `briefRefusal` first and say the sentence it returns,
 * and the store's writer clamps as the belt behind them.
 *
 * `at` is stamped here too (`stampDirectives`): when a directive joined the
 * brief. A brief that changes mid-exploration was being reported as "directives
 * the user set for every pass" — a given typed on the third day read as though
 * the first day's passes had carried it. The report, the Overview and the PDF
 * date a directive that arrived after passes had run (`briefLateNote`); each
 * pass's own receipt names exactly what it was sent.
 *
 * And what the brief used to say (`briefWrite`): a directive can be reworded in
 * place, and removed, on any day, and the passes before either were briefed with
 * words the brief no longer holds. Those words are kept, dated, beside it
 * (`Session.formerDirectives`), so the thread can show each change where it fell
 * between passes and the exports can say "edited" where "added" would claim the
 * earlier passes saw the new words.
 *
 * Imports only types and one label table at runtime, so the codec, the store and
 * the composer can all read it.
 */

import {
  DIRECTIVE_LABEL,
  type Directive,
  type DirectiveKind,
  type FormerDirective,
  type Op,
  type Session,
} from './types'

/** Directives kept per document. A brief is a few lines the user typed, not a
 *  corpus — a document claiming more was hand-edited. */
export const MAX_BRIEF = 12
/** Characters kept per directive — the codec's clamp, said at the field. */
export const DIRECTIVE_CHARS = 240
/** Former directives kept per document, the newest. Twice the brief: enough for
 *  every directive to have been reworded or removed once, and a bound on words
 *  nothing is sent. Past it the oldest go, and the thread shows the directive
 *  they led to as added on the day of its edit. */
export const MAX_FORMER = 2 * MAX_BRIEF

/**
 * Why these directives cannot join this brief, or null when they can.
 *
 * Refused whole rather than trimmed to fit: which of three typed givens is the
 * one to drop is the reader's call, and a brief that kept the first two would
 * steer every later pass with a stipulation set nobody chose.
 */
export function briefRefusal(
  current: readonly Directive[] | undefined,
  adding: readonly Directive[],
): string | null {
  const have = current?.length ?? 0
  if (have + adding.length > MAX_BRIEF) {
    const room = Math.max(0, MAX_BRIEF - have)
    return have >= MAX_BRIEF
      ? `The standing brief holds ${MAX_BRIEF} directives, and it is full. Remove one first — Adjust, under Standing brief, lists them with a ✕ each.`
      : `The standing brief holds ${MAX_BRIEF} directives and has room for ${room} more, so these ${adding.length} would not all be kept. Remove one, or add fewer.`
  }
  const long = adding.find((d) => d.text.length > DIRECTIVE_CHARS)
  if (long)
    return `A directive keeps ${DIRECTIVE_CHARS} characters, and “${long.text.slice(0, 24)}…” has ${long.text.length}, so its end would be cut. Shorten it first.`
  return null
}

/** Directives as they join the brief: stamped with when, unless they carry one. */
export function stampDirectives(adding: readonly Directive[], at: number): Directive[] {
  return adding.map((d) => (d.at === undefined ? { ...d, at } : d))
}

/**
 * The line a directive gets in an export when it joined after passes had run:
 * "added 2026-09-14, after 3 passes" — or null when it was there from the
 * first pass, or its arrival is not on record (a directive stored before `at`
 * existed, whose day nobody can recover and nobody should guess).
 *
 * Counted off the stored operations, as a pass that STARTED before the
 * directive arrived: that pass was briefed without it, whatever it went on to
 * produce. `day` is the caller's formatter, so every surface prints its own
 * dates in the one form (`core/dates.ts`).
 */
export function briefLateNote(
  s: Pick<Session, 'ops' | 'formerDirectives'>,
  d: Directive,
  day: (t: number) => string,
): string | null {
  if (d.at === undefined) return null
  const before = s.ops.filter((o) => o.startedAt < d.at! && !o.narration).length
  if (!before) return null
  // Reworded, the directive was in the brief before these passes, in other words:
  // "added" would say they had nothing, and the report's lead would say they had
  // these.
  const verb = editedFrom(s, d) ? 'edited' : 'added'
  return `${verb} ${day(d.at)}, after ${before} pass${before === 1 ? '' : 'es'}`
}

/** Whether any directive in the brief joined after passes had run — the case in
 *  which "for every pass" stops being true of the list. */
export function briefChangedMidway(
  s: Pick<Session, 'ops' | 'brief' | 'formerDirectives'>,
): boolean {
  return (s.brief ?? []).some((d) => briefLateNote(s, d, String) !== null)
}

/**
 * What the exports say of the brief after "The standing brief —", in the one
 * place both formats read it: "for every pass" only while that is true, and
 * "added or edited" once a late directive was reworded rather than new, because
 * the passes before an edit were briefed with the directive, in other words.
 */
export function briefLeadClause(s: Pick<Session, 'ops' | 'brief' | 'formerDirectives'>): string {
  if (!briefChangedMidway(s)) return 'directives the user set for every pass:'
  const lateEdit = (s.brief ?? []).some(
    (d) => editedFrom(s, d) !== undefined && briefLateNote(s, d, String) !== null,
  )
  return lateEdit
    ? 'directives the user set. A directive added or edited after passes had run is dated; the passes before that were not briefed with its words:'
    : 'directives the user set. A directive added after passes had run is dated; the passes before it were not briefed with it:'
}

/**
 * The words this directive's current words replaced, when an edit put them there
 * — or undefined for a directive that joined as it stands. Read back by the
 * one join the writer keeps exact: the entry's `left` is the directive's `at`.
 * Works on a former entry as well as a directive in force, which is how a chain
 * of edits is walked.
 */
export function editedFrom(
  s: Pick<Session, 'formerDirectives'>,
  d: Pick<Directive, 'kind' | 'text' | 'at'>,
): FormerDirective | undefined {
  if (d.at === undefined) return undefined
  return s.formerDirectives?.find(
    (f) => f.left === d.at && f.kind === d.kind && f.replacedBy === d.text,
  )
}

/** One directive as a change to the brief names it: its kind, its words, and —
 *  for an edit — the words it had before. */
export interface BriefLine {
  kind: DirectiveKind
  text: string
  was?: string
}

/** What happened to the brief at one instant: the directives that joined it
 *  together (a seed's, or one press's), an edit, or a removal. */
export interface BriefChange {
  at: number
  what: 'added' | 'edited' | 'removed'
  lines: BriefLine[]
}

/**
 * Every change to the brief the document dates, oldest first — the exploration's
 * thread sets each one between the passes it fell between (`core/feed.ts`).
 *
 * Read off the dates the brief already keeps and nothing else: a directive's
 * `at` for its arrival, a former entry's `at` and `left` for the words that went.
 * An arrival that an edit put there is not an arrival — the edit's own row names
 * both wordings — and an undated directive has no row, because its day is not on
 * record and is never guessed. Directives that arrived in one write share a row.
 */
export function briefChanges(s: Pick<Session, 'brief' | 'formerDirectives'>): BriefChange[] {
  const rows = new Map<string, BriefChange>()
  const put = (what: BriefChange['what'], at: number, line: BriefLine) => {
    const key = `${what}@${at}`
    const row = rows.get(key)
    if (row) row.lines.push(line)
    else rows.set(key, { at, what, lines: [line] })
  }
  for (const v of [...(s.brief ?? []), ...(s.formerDirectives ?? [])]) {
    if (v.at === undefined || editedFrom(s, v)) continue
    put('added', v.at, { kind: v.kind, text: v.text })
  }
  for (const f of s.formerDirectives ?? []) {
    if (f.replacedBy !== undefined)
      put('edited', f.left, { kind: f.kind, text: f.replacedBy, was: f.text })
    else put('removed', f.left, { kind: f.kind, text: f.text })
  }
  return [...rows.values()].sort((a, b) => a.at - b.at)
}

/** Whether a pass started while words that joined at `from` stood, up to `to` —
 *  the passes `briefLateNote` counts as after them. Undated words may have
 *  briefed any pass before they left. Narrations are not passes. */
function briefedAPass(ops: readonly Op[], from: number | undefined, to: number): boolean {
  return ops.some(
    (o) => !o.narration && o.startedAt < to && (from === undefined || o.startedAt >= from),
  )
}

/**
 * The brief with directive `i` reworded, for `setBrief` — or null when the words
 * are empty or unchanged, which is not an edit. Its kind and its gap stay; its
 * date does not travel, because the writer dates the words (`briefWrite`).
 */
export function reworded(brief: readonly Directive[], i: number, text: string): Directive[] | null {
  const d = brief[i]
  const words = text.trim()
  if (!d || !words || words === d.text) return null
  const { at: _dated, ...kept } = d
  return brief.map((x, j) => (j === i ? { ...kept, text: words.slice(0, DIRECTIVE_CHARS) } : x))
}

/**
 * The store's one write of the brief, as data: what `setBrief` sets, given what
 * the session held and the list it was handed.
 *
 * Directives are told apart by identity, as the store always has: one the list
 * already held keeps what it had, including no date at all; one it did not is
 * dated as it joins. A write that takes one directive out and puts one of the same
 * kind in its place is an EDIT — the one shape `reworded` makes, and no other door
 * writes it — and the directive keeps its place in the list.
 *
 * Words that leave are kept in `formerDirectives`, dated, when a pass was briefed
 * with them. Words no pass was sent leave no trace: a typo corrected before the
 * next pass, or a directive added and taken back with the toast's Undo, steered
 * nothing, and a thread that showed them would be showing keystrokes. Then the
 * directive reads as though its next words, or its removal, came when those did:
 * an edit keeps their date, and the entry that led to them points past them — or,
 * where the edit puts back the very words that entry holds (the edit toast's
 * Undo), the entry goes and the directive is again what it was, date and all.
 */
export function briefWrite(
  s: Pick<Session, 'brief' | 'formerDirectives' | 'ops'>,
  next: readonly Directive[],
  now: number,
): { brief: Directive[]; former: FormerDirective[] } {
  const prev = s.brief ?? []
  const held = new Set(prev)
  const capped = next.slice(0, MAX_BRIEF)
  const kept = new Set(capped)
  const gone = prev.flatMap((d, i) => (kept.has(d) ? [] : [{ d, i }]))
  const came = capped.flatMap((d, i) => (held.has(d) ? [] : [{ d, i }]))
  const edit =
    gone.length === 1 &&
    came.length === 1 &&
    gone[0]!.i === came[0]!.i &&
    gone[0]!.d.kind === came[0]!.d.kind
      ? { from: gone[0]!.d, to: came[0]!.d }
      : null

  let former = [...(s.formerDirectives ?? [])]
  // When the edited directive's words are dated: now, unless the words it
  // replaces briefed nothing, and then theirs (which may be no date at all).
  let editAt: number | undefined = now
  for (const { d } of gone) {
    const replacedBy = edit?.from === d ? edit.to.text : undefined
    if (briefedAPass(s.ops, d.at, now)) {
      former.push({
        kind: d.kind,
        text: d.text,
        ...(d.at !== undefined ? { at: d.at } : {}),
        left: now,
        ...(replacedBy !== undefined ? { replacedBy } : {}),
      })
      continue
    }
    if (replacedBy !== undefined) editAt = d.at
    const origin = editedFrom({ formerDirectives: former }, d)
    if (!origin) continue
    if (replacedBy !== undefined && replacedBy === origin.text) {
      former = former.filter((f) => f !== origin)
      editAt = origin.at
      continue
    }
    const { replacedBy: _was, ...rest } = origin
    former = former.map((f) =>
      f === origin ? (replacedBy !== undefined ? { ...rest, replacedBy } : rest) : f,
    )
  }

  const brief = capped.map((d) => {
    if (held.has(d)) return d
    if (d === edit?.to) {
      const { at: _any, ...words } = d
      return editAt !== undefined ? { ...words, at: editAt } : words
    }
    return d.at !== undefined ? d : { ...d, at: now }
  })
  return { brief, former: former.slice(-MAX_FORMER) }
}

/**
 * The toast a typed directive earns when it joins the brief: what it became, and
 * the one clause a given always carries. The same promise "Answer it" makes, so a
 * directive reads the same whichever door it came through.
 */
export function briefAddedToast(adding: readonly Directive[]): string {
  const given = adding.some((d) => d.kind === 'given')
  const tail = given ? ' — a given is stipulated, never certified' : ''
  if (adding.length === 1) {
    const d = adding[0]!
    return `Added to your standing brief — ${DIRECTIVE_LABEL[d.kind]}: “${d.text}”. Every pass from here is briefed with it${tail}.`
  }
  return `Added ${adding.length} directives to your standing brief. Every pass from here is briefed with them${tail}.`
}

/**
 * The toast an edit earns: the words in force now, and what that means for the
 * passes on either side of it — the ones already made keep the words they were
 * sent, which is what the thread's row and the report's date then say.
 */
export function briefEditedToast(d: Directive): string {
  const tail = d.kind === 'given' ? ' — a given is stipulated, never certified' : ''
  return `Reworded in your standing brief — ${DIRECTIVE_LABEL[d.kind]}: “${d.text}”. Every pass from here is briefed with these words, and the passes before keep the ones they were sent${tail}.`
}

/**
 * The brief a branch carries: every directive, in the reader's words and with the
 * date it joined, and an answer to a gap the branch does not carry as a directive
 * of its own.
 *
 * Kept, not dropped. A gap's answer is a stipulation about the question — "output
 * means revenue per employee" — and says so whichever claim prompted it; the
 * passes the branch carries were briefed with it wherever it arrived before them,
 * and a branch that shed it would brief its own first pass differently from the
 * ones it inherited, with no press and nothing on screen to say so. Which
 * directive to drop is the reader's call (`briefRefusal` refuses whole for the
 * same reason), and in a branch it is one ✕ under Adjust, where a dropped one
 * could not come back: history does not cross a fork.
 *
 * The link goes, because it names a claim outside the cut, and the fork's own
 * rule for a live reference past the cut is to leave it behind — a bridge whose
 * end is gone, a claim scope, a contradiction analysis (`forkAtStep`,
 * `researchForFork`). Nothing on screen reads a link to a missing node, but the
 * manifest carries the brief whole, and there it was an id no row in the file
 * could resolve.
 */
export function briefForBranch(
  brief: readonly Directive[],
  carried: ReadonlySet<string>,
): Directive[] {
  return brief.map((d) => {
    if (d.answers === undefined || carried.has(d.answers)) return d
    const { answers: _left, ...own } = d
    return own
  })
}

/** "Given: the catalyst is stable", as the tune panel and the chip's title print a
 *  directive. */
export const directiveLine = (d: Directive): string => `${DIRECTIVE_LABEL[d.kind]}: ${d.text}`
